import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { alertsAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Mail, CheckCircle2, XCircle, Send, Loader2, Settings as SettingsIcon } from 'lucide-react';
import B2BPortalSettings from '../components/settings/B2BPortalSettings';
import SystemHealthCard from '../components/settings/SystemHealthCard';
import ScanDefaultsCard from '../components/settings/ScanDefaultsCard';
import ResetTestDataCard from '../components/settings/ResetTestDataCard';

/**
 * Admin-only Settings page. Currently focused on email/Resend configuration —
 * future home for integrations, defaults, currency settings, etc.
 */
const Settings = () => {
    const { t } = useTranslation(['common']);
    const { isAdmin } = useAuth();
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [testTo, setTestTo] = useState('');
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (!isAdmin) return;
        (async () => {
            try {
                const { data } = await alertsAPI.emailStatus();
                setStatus(data);
            } catch (err) {
                setStatus({ configured: false, error: err?.response?.data?.detail || 'Failed to read status' });
            } finally {
                setStatusLoading(false);
            }
        })();
    }, [isAdmin]);

    const sendTest = async () => {
        if (!testTo.trim()) {
            toast.error(t('common:toasts.email_required'));
            return;
        }
        setSending(true);
        try {
            await alertsAPI.sendTestEmail(testTo.trim());
            toast.success(t('common:toasts.email_sent'));
        } catch (err) {
            toast.error(err?.response?.data?.detail || t('common:toasts.email_failed'));
        } finally {
            setSending(false);
        }
    };

    if (!isAdmin) {
        return (
            <div className="p-8 text-center text-gray-500" data-testid="settings-no-access">
                Admin access required
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fadeIn max-w-4xl" data-testid="settings-page">            <div>
                <p className="text-[11px] uppercase tracking-[2px] text-[#C08478] font-semibold mb-2">
                    {t('common:section.admin', { defaultValue: 'Admin' })}
                </p>
                <h1 className="text-3xl font-bold text-[#1A1A1A] tracking-tight flex items-center gap-2">
                    <SettingsIcon size={24} className="text-[#C08478]" />
                    {t('common:section.settings', { defaultValue: 'Settings' })}
                </h1>
                <p className="text-gray-500 mt-1">
                    {t('common:section.settings_subtitle', { defaultValue: 'Manage integrations and platform configuration.' })}
                </p>
            </div>

            {/* Email / Resend card */}
            <Card className="border-0 shadow-sm" data-testid="email-status-card">
                <CardHeader>
                    <CardTitle className="text-base flex items-center gap-2">
                        <Mail size={18} className="text-[#C08478]" />
                        Email delivery (Resend)
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {statusLoading ? (
                        <Loader2 size={20} className="animate-spin text-gray-400" />
                    ) : (
                        <>
                            <div className="flex items-center gap-3" data-testid="email-config-status">
                                {status?.configured ? (
                                    <Badge className="bg-[#2A9D8F]/15 text-[#2A9D8F] gap-1">
                                        <CheckCircle2 size={12} /> Connected
                                    </Badge>
                                ) : (
                                    <Badge className="bg-[#E76F51]/15 text-[#E76F51] gap-1">
                                        <XCircle size={12} /> Not configured
                                    </Badge>
                                )}
                                <span className="text-xs text-gray-500">
                                    {status?.configured
                                        ? `Sender: ${status.sender_email}`
                                        : 'Add RESEND_API_KEY to your backend .env'}
                                </span>
                            </div>

                            {/* Test email */}
                            <div className="space-y-2 border-t pt-4">
                                <Label htmlFor="test-email-to" className="text-sm">
                                    Send a test email
                                </Label>
                                <div className="flex gap-2">
                                    <Input
                                        id="test-email-to"
                                        type="email"
                                        value={testTo}
                                        onChange={(e) => setTestTo(e.target.value)}
                                        placeholder="you@example.com"
                                        data-testid="test-email-input"
                                        className="max-w-sm"
                                    />
                                    <Button
                                        onClick={sendTest}
                                        disabled={sending || !status?.configured}
                                        data-testid="test-email-btn"
                                        className="bg-[#1A1A1A] hover:bg-[#333] text-white"
                                    >
                                        {sending
                                            ? <Loader2 size={14} className="animate-spin mr-2" />
                                            : <Send size={14} className="mr-2" />}
                                        Send
                                    </Button>
                                </div>
                                <p className="text-[11px] text-gray-400">
                                    Use this to verify your Resend setup. The email goes out from{' '}
                                    <span className="font-mono">{status?.sender_email}</span>.
                                </p>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* B2B Portal — brand visibility + future portal-wide config */}
            <B2BPortalSettings />

            {/* System Health — live traffic-light dashboard for admin ops */}
            <SystemHealthCard />

            {/* Barcode scan default quantity */}
            <ScanDefaultsCard />

            {/* Danger zone — one-time / on-demand transactional data wipe. Backed by POST /api/admin/cleanup. */}
            <ResetTestDataCard />
        </div>
    );
};

export default Settings;
